import { useState } from 'react'
import { useQuery } from 'react-query'

import { StrongNode, nodes } from 'prose-core'
import { useInterval } from 'beautiful-react-hooks'
import { JiraClient } from 'api/clients/JiraClient'
import { JiraIssueCard } from './JiraIssueCard'
import { JiraIssueInline } from './JiraIssueInline'
import { JiraIssueFrame } from './JiraIssueFrame'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { FetchError } from './common'
import { EmbedPrivate } from '../EmbedCommon/EmbedPrivate'
import { ReactComponent as JiraIcon } from 'assets/icons/logos/jira.svg'
import { EmbedError } from '../EmbedCommon/EmbedError'
import store from 'redux/store'
import { JiraIssue as JiraIssueType } from './types'
import { isObjectWithProperty } from 'types/unknown'

export const JiraIssue = ({ node, preview }: { node: StrongNode<typeof nodes.jira_embed>; preview?: true }) => {
  const [shouldRetry, setShouldRetry] = useState(false)
  const maybeArticle = store.getState().currentArticleId
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      store.getState().articleMeta[store.getState().currentArticleId!]
    : null
  const id = node.attrs.issueId

  const { isLoading, data, isError, error, refetch } = useQuery(
    [maybeArticle?.company_id, id],
    async () => {
      if (!maybeArticle?.company_id) {
        throw new Error('missing company_id (are you inside a workspace?)')
      }

      const jiraClientResponse = await JiraClient.getIssue({
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspaceId: maybeArticle!.company_id!,
        id,
      })

      if (jiraClientResponse.type === SUCCESS_RESPONSE_TYPE) {
        return jiraClientResponse.data
      }

      if (jiraClientResponse.type === 'error') {
        throw new FetchError(jiraClientResponse, 'Error')
      }
    },
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: () => {
        if (isError) {
          // if the request is not successful, refetchOnWindowFocus only if the timeout has elapsed
          if (shouldRetry) {
            setShouldRetry(false)
            return true
          } else {
            return false
          }
        }
        // if the request is successful, respect staleTime
        return true
      },
    },
  )

  useInterval(() => {
    if (isError) {
      setShouldRetry(true)
    }
  }, 10 * 60 * 1000)

  if (isError && [401, 404].includes((error as FetchError)?.res?.status)) {
    return <EmbedPrivate mode={node.attrs.mode} link={node.attrs.link} preview={preview} icon={JiraIcon} />
  } else if (isError || (!isLoading && (!data || !isObjectWithProperty<string, JiraIssueType>('fields', data)))) {
    return <EmbedError refetch={refetch} mode={node.attrs.mode} link={node.attrs.link} preview={preview} />
  }

  switch (node?.attrs.mode) {
    default:
    case 'frame':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <JiraIssueFrame node={node} data={data!} isLoading={isLoading} preview={preview} />
    case 'card':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <JiraIssueCard node={node} data={data!} isLoading={isLoading} preview={preview} />
    case 'inline':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <JiraIssueInline node={node} data={data!} isLoading={isLoading} preview={preview} />
  }
}
